import { Mutex } from 'async-mutex'
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { ApiResponse, ApisauceInstance } from '~/apisauce'
import Logger from '~/services/Logger'
import {
  ApiClientAuthData,
  AuthenticationResultCallback,
  AuthenticationType,
} from '~/types/MangoApiClientTypes'

import { ApiError, AppSessionJwts } from '@mangovoice/types'

export enum ApiTokenType {
  Jwt = 'jwt',
  Session = 'session',
}

type MangoApiClientResponse<T> = Promise<ApiResponse<T, ApiError>>

const mutex = new Mutex()
const logger = new Logger('authenticationInterceptor')
const gatewayInterceptor = async (cfg: InternalAxiosRequestConfig) => {
  if (mutex.isLocked()) {
    await mutex.waitForUnlock()
  }

  return cfg
}

const authInterceptor = async (
  error: AxiosError,
  authResultCallback: AuthenticationResultCallback | undefined,
  getAuthData: () => ApiClientAuthData | undefined,
  getRefreshToken: () => MangoApiClientResponse<AppSessionJwts>,
) => {
  const release = await mutex.acquire()
  try {
    const responseCode = error.response?.status ?? 0
    //this callback is for auth interception alone, act only on 401/403
    if (responseCode !== 401) return await Promise.reject(error)

    //authorization was denied, attempt to refresh token and session
    //if there's no authData available, do nothing
    const authData = getAuthData()
    if (!authData?.refreshToken) {
      authResultCallback?.({
        authenticationType: AuthenticationType.RefreshAppSessionJwts,
        ok: false,
        result: {
          code: 'no_auth_data',
          message: 'No auth data available',
        },
      })
      return await Promise.reject(error)
    }

    //first check to see if the session token on the request is the same as the current set of auth data,
    //because interceptors run synchronously, there may be an update, if there is, attempt to retry the request now
    let newTokenHeaderValue = `Bearer ${authData?.accessToken}`
    const curAuthHeaderVal = error.config?.headers.Authorization
    if (curAuthHeaderVal !== newTokenHeaderValue) {
      //token was updated since the request was made, retry the request now
      const cfg = { ...error.config }
      if (!cfg.headers) return await Promise.reject(error)
      logger.info('Token changed, retrying request')
      cfg.headers.Authorization = newTokenHeaderValue
      return await axios.request({
        ...cfg,
        ...{
          headers: cfg.headers.toJSON(),
        },
      })
    }

    //attempt to reauthenticate with the user's refresh token
    const refreshResult = await getRefreshToken()
    if (!refreshResult.ok) {
      logger.info('Failed to refresh token')
      return await Promise.reject(error)
    }

    //all worked, retry the original request
    const cfg = { ...error.config }
    newTokenHeaderValue = `Bearer ${getAuthData()?.accessToken}`
    if (!cfg.headers) return await Promise.reject(error)
    cfg.headers.Authorization = newTokenHeaderValue

    logger.info('Token refreshed, retrying request')
    //passing in config and forcing headers to json to avoid axios error
    return await axios.request({
      ...cfg,
      ...{
        headers: cfg.headers.toJSON(),
      },
    })
  } catch {
    return await Promise.reject(error)
  } finally {
    release()
  }
}

export const setAuthInterceptors = (
  apiSauceInstances: {
    apiSauceInstance: ApisauceInstance
    tokenType: ApiTokenType
  }[],
  authResultCallback: AuthenticationResultCallback | undefined,
  getAuthDataFunc: () => ApiClientAuthData | undefined,
  getRefreshTokenFunc: () => MangoApiClientResponse<AppSessionJwts>,
) => {
  apiSauceInstances.forEach((instanceData) => {
    const { apiSauceInstance } = instanceData
    apiSauceInstance.axiosInstance.interceptors.request.use(gatewayInterceptor)

    apiSauceInstance.axiosInstance.interceptors.response.use(
      undefined,
      (error) =>
        authInterceptor(
          error,
          authResultCallback,
          getAuthDataFunc,
          getRefreshTokenFunc,
        ),
    )
  })
}
